import React from 'react'
import styled from '@emotion/styled'
import { string } from 'prop-types'
import QueryString from 'qs'

const Wrapper = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;
	height: auto;
	margin: 2rem auto 4rem auto;
	max-width: 650px;
	position: relative;
	width: 90%;

	${({ theme }) => theme.MQ.M} {
		margin-top: 6rem;
		max-width: 950px;
		width: 95%;
	}

	${({ theme }) => theme.MQ.XL} {
		max-width: 1000px;
	}
`
const Title = styled.h1`
	${({ theme }) => theme.fonts.bold};
	color: ${({ theme }) => theme.colors.primary};
	display: flex;
	font-size: 1.6rem;
	letter-spacing: 0;
	line-height: 32px;
	margin: 0 auto 2rem auto;

	text-align: center;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		font-size: 2.625rem;
		letter-spacing: -1.71px;
		line-height: 60px;
		margin-bottom: 2rem;
	}

	${({ theme }) => theme.MQ.XL} {
		font-size: 3.375rem;
		letter-spacing: -2.2px;
		line-height: 60px;
		margin-bottom: 4rem;
		text-align: center;
	}
`
const HeroWrapper = styled.div`
	align-items: center;
	display: flex;
	flex-direction: column;

	${({ theme }) => theme.MQ.M} {
		flex-direction: row;
		max-width: 1000px;
	}
`
const HeroImage = styled.img`
	display: none;
	height auto;
	width: 100%;
	
	${({ theme }) => theme.MQ.M} {
		display: block;
		max-width: 50%;
	}
	
	${({ theme }) => theme.MQ.L} {
		max-width: 550px;
	}
`
const FormContainer = styled.div`
	background-color: ${({ hasBackground, theme }) => (hasBackground ? theme.colors.gray[100] : theme.colors.white)};
	border-radius: 16px;
	display: flex;
	flex-direction: column;
	justify-content: center;
	align-items: center;
	padding: 0.5rem 1.5rem 2rem;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		padding: 1.5rem 3rem 2rem;
		max-width: 520px;
	}
`
const Question = styled.div`
	${({ theme }) => theme.fonts.primarySemiBold};
	color: ${({ theme }) => theme.colors.tertiary};
	font-size: 1.125rem;
	letter-spacing: 0.75px;
	line-height: 26px;
	margin: 1rem auto 1.5rem;
	text-align: center;

	${({ theme }) => theme.MQ.M} {
		font-size: 1.25rem;
		letter-spacing: 0.83px;
		line-height: 34px;
		margin: 0 auto 3rem;
		max-width: 550px;
	}

	${({ theme }) => theme.MQ.L} {
		font-size: 1.5rem;
		letter-spacing: 1px;
	}
`
const ButtonsContainer = styled.div`
	${({ theme }) => theme.fonts.bold};
	max-width: 400px;
	text-transform: uppercase;
	width: 100%;
`
const YesNoContainer = styled.div`
	display: flex;
	gap: 1rem;
	font-size: 1.3275rem;
	letter-spacing: 0.89px;
	line-height: 28.86px;
	text-align: center;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		font-size: 1.75rem;
		letter-spacing: 1.17px;
		line-height: 38.04px;
	}
`
const Yes = styled.a`
	align-items: center;
	border: 3.28px solid ${({ theme }) => theme.colors.gray[300]};
	border-radius: 5.25px;
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: 0 2px 22px 0 rgba(0, 0, 0, 0.15);
	color: ${({ theme }) => theme.colors.tertiary};
	display: flex;
	justify-content: center;
	height: 65px;
	text-decoration: none;
	width: 50%;

	${({ theme }) => theme.MQ.M} {
		border: 4.32px solid ${({ theme }) => theme.colors.gray[300]};
		border-radius: 6.92px;
		background-color: ${({ theme }) => theme.colors.white};
		box-shadow: 0 2px 29px 0 rgba(0, 0, 0, 0.15);
		height: 84px;
	}
`
const No = styled.a`
	align-items: center;
	border: 3.28px solid ${({ theme }) => theme.colors.gray[300]};
	border-radius: 5.25px;
	background-color: ${({ theme }) => theme.colors.white};
	box-shadow: 0 2px 22px 0 rgba(0, 0, 0, 0.15);
	color: ${({ theme }) => theme.colors.tertiary};
	display: flex;
	justify-content: center;
	height: 65px;
	text-decoration: none;
	width: 50%;

	${({ theme }) => theme.MQ.M} {
		border: 4.32px solid ${({ theme }) => theme.colors.gray[300]};
		border-radius: 6.92px;
		background-color: ${({ theme }) => theme.colors.white};
		box-shadow: 0 2px 29px 0 rgba(0, 0, 0, 0.15);
		height: 84px;
	}
`
const GetQuotes = styled.a`
	align-items: center;
	background: linear-gradient(116.53deg, #f68e53 0%, #f06f3b 100%);
	border-radius: 5.25px;
	color: ${({ theme }) => theme.colors.white};
	display: flex;
	justify-content: center;
	font-size: 24.28px;
	height: 65px;
	letter-spacing: 3.24px;
	line-height: 35px;
	margin-top: 1.5rem;
	text-align: center;
	text-decoration: none;
	width: 100%;

	${({ theme }) => theme.MQ.M} {
		font-size: 2rem;
		letter-spacing: 4.27px;
		line-height: 48px;
		height: 84px;
	}
`

CallToActionSection.propTypes = {
	backgroundImg: string,
	question: string,
	title: string,
}

/**
 * Call To Action form for Leave Behind Page Template
 * Consists of title, question, yes/no answer form and option background images
 *
 * @param {String} props.backgroundImg :: image path for hero image
 * @param {String} props.className :: pass any override styles
 * @param {String} props.question :: question for the CTA form
 * @param {String} props.title :: title for the CTA form
 */
function CallToActionSection({ backgroundImg, className, question, title }) {
	const redirectToLeaveBehindPage = () => {
		const currentUrl = window.location.href
		const parsedUrl = QueryString.parse(currentUrl?.split('?'))
		const originUrl = parsedUrl[0]
		const getUrlParams = parsedUrl[1]
		const newUrl = `${originUrl}offers/${getUrlParams ? `?${getUrlParams}` : ''}`

		// redirect to https://www.business.com/lp/compare-business-phone-systems/offers/{params} but make it work on stg and branch
		setTimeout(function () {
			window.location.replace(newUrl)
		}, 200)
	}
	return (
		<Wrapper className={className}>
			<Title>{title}</Title>
			<HeroWrapper>
				{backgroundImg && <HeroImage className="lazyload" src={backgroundImg} />}
				<FormContainer hasBackground={!!backgroundImg}>
					<Question>{question}</Question>
					<ButtonsContainer>
						<YesNoContainer>
							<Yes href="./form" onClick={redirectToLeaveBehindPage} target="_blank">
								Yes
							</Yes>
							<No href="./form" onClick={redirectToLeaveBehindPage} target="_blank">
								No
							</No>
						</YesNoContainer>
						<GetQuotes href="./form" onClick={redirectToLeaveBehindPage} target="_blank">
							Get Quotes
						</GetQuotes>
					</ButtonsContainer>
				</FormContainer>
			</HeroWrapper>
		</Wrapper>
	)
}

export default CallToActionSection
